import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ShieldAlert,
  Lock,
  KeyRound,
  CreditCard,
  Bell,
  UserCheck,
  Activity,
  Clock,
  SignalHigh,
  Globe2,
  Route as RouteIcon,
  Filter,
  BadgeCheck,
  Eye,
  Gauge,
  Layers,
  Network,
  Headphones,
  MessageSquare,
  Smartphone,
  Mail,
  CheckCircle,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";

const whyItMatters = [
  {
    title: "Suspicious Transaction Alerts",
    description:
      "When a risk engine flags a payment as unusual, a text message reaches the customer within seconds so they can confirm or reject it while the transaction is still recent.",
    Icon: ShieldAlert,
    color: "text-rose",
  },
  {
    title: "Unusual Login Notifications",
    description:
      "A sign-in from a new device, browser or location can be surfaced to the account holder immediately, giving them the chance to react rather than discover it later.",
    Icon: KeyRound,
    color: "text-amber",
  },
  {
    title: "Account Security Alerts",
    description:
      "Changes to contact details, limits or linked beneficiaries are activity customers want to hear about the moment they happen.",
    Icon: Lock,
    color: "text-indigo",
  },
  {
    title: "Payment Verification Requests",
    description:
      "Where an action needs a second confirmation before it completes, SMS carries the request or code to a device the customer already has.",
    Icon: CreditCard,
    color: "text-sky",
  },
  {
    title: "Credential Change Alerts",
    description:
      "Password, PIN and email change notifications are often the first signal that an account has been taken over.",
    Icon: BadgeCheck,
    color: "text-violet",
  },
  {
    title: "Account Recovery Notifications",
    description:
      "Recovery and reset flows are a common target, so messaging that confirms each step helps the legitimate owner notice attempts they did not start.",
    Icon: UserCheck,
    color: "text-lime",
  },
  {
    title: "High-Risk Activity Notifications",
    description:
      "Large transfers, first-time payees, unusual spending patterns or activity from an unexpected market can each trigger a short, clear notification.",
    Icon: Activity,
    color: "text-navy",
  },
  {
    title: "Additional Verification Steps",
    description:
      "When a risk score sits in the middle, SMS supports a step-up check instead of blocking the customer outright or letting the action through unchecked.",
    Icon: Layers,
    color: "text-pink",
  },
];

const challenges = [
  {
    title: "Delayed Alerts",
    description:
      "A security notification is only useful while the customer can still act on it. Latency in the delivery path turns a timely warning into an after-the-fact record.",
    Icon: Clock,
    color: "text-amber",
  },
  {
    title: "Failed Delivery",
    description:
      "Messages that never reach the handset leave the customer uninformed and push them towards the contact centre, often after they have noticed the activity themselves.",
    Icon: SignalHigh,
    color: "text-rose",
  },
  {
    title: "Inconsistent International Delivery",
    description:
      "A route that performs well in one market can behave differently in another, so businesses with customers in several countries often see quality vary by destination.",
    Icon: Globe2,
    color: "text-sky",
  },
  {
    title: "SMS Filtering",
    description:
      "Operator and regulatory filtering can hold or block traffic based on content, sender or sending pattern, and security wording sometimes attracts more scrutiny than expected.",
    Icon: Filter,
    color: "text-violet",
  },
  {
    title: "Sender ID Issues",
    description:
      "Sender ID rules differ between countries. Where identifiers must be registered or are overwritten, the alert can look unfamiliar and be ignored by the recipient.",
    Icon: BadgeCheck,
    color: "text-indigo",
  },
  {
    title: "Route Quality",
    description:
      "Traffic passed through long chains of intermediaries is less predictable and harder to investigate, which is a poor fit for messages sent under time pressure.",
    Icon: RouteIcon,
    color: "text-lime",
  },
  {
    title: "Lack of Delivery Visibility",
    description:
      "Without meaningful delivery reporting, teams cannot tell whether the problem sits in their own platform, the messaging provider or the destination network.",
    Icon: Eye,
    color: "text-navy",
  },
  {
    title: "Destination-Specific Troubleshooting",
    description:
      "Understanding why one market underperforms takes operator-level knowledge and a provider willing to look at the route rather than close the ticket.",
    Icon: Gauge,
    color: "text-pink",
  },
];

const howWeSupport = [
  {
    title: "Telecom-Native Expertise",
    description:
      "Yootelco comes from the telecommunications side of messaging, with more than 17 years of experience in voice and messaging. That background shapes how security traffic is built and investigated.",
    Icon: Network,
    color: "text-indigo",
  },
  {
    title: "Quality Routing",
    description:
      "Security and authentication traffic is routed through connections chosen for reliability rather than lowest cost, with decisions made per destination and traffic type.",
    Icon: RouteIcon,
    color: "text-amber",
  },
  {
    title: "Global Connectivity",
    description:
      "Alerts are delivered through operator and carrier connectivity across the markets our clients serve, supporting consistent reach as customer bases expand.",
    Icon: Globe2,
    color: "text-sky",
  },
  {
    title: "Monitoring and Operational Attention",
    description:
      "Traffic is monitored so performance changes are noticed and acted on, and so your team has delivery insight to work from when investigating an issue.",
    Icon: Activity,
    color: "text-lime",
  },
  {
    title: "Responsive Client Support",
    description:
      "A team built for enterprise clients works directly with your engineering, risk and operations people instead of routing you through a ticket queue.",
    Icon: Headphones,
    color: "text-violet",
  },
  {
    title: "Practical Troubleshooting",
    description:
      "When a route looks wrong we investigate it at operator level and change it, rather than leaving traffic in place and reporting the same numbers back.",
    Icon: Gauge,
    color: "text-rose",
  },
];

const useCases = [
  {
    title: "Suspicious Transaction Alerts",
    description:
      "Notify a customer when a payment or transfer looks out of pattern and let them confirm or dispute it before further activity follows.",
    Icon: ShieldAlert,
    color: "text-rose",
    href: "/use-cases/transactional-alerts",
    linkLabel: "Transactional alerts",
  },
  {
    title: "Unusual Login Alerts",
    description:
      "Tell the account holder about sign-ins from an unrecognised device or location so an unauthorised session can be shut down quickly.",
    Icon: KeyRound,
    color: "text-amber",
    href: "/solutions/two-factor-authentication",
    linkLabel: "Two-factor authentication",
  },
  {
    title: "Account Security Notifications",
    description:
      "Confirm security-relevant changes such as contact details, limits or linked payees, giving the customer a chance to flag anything they did not request.",
    Icon: Lock,
    color: "text-indigo",
    href: "/solutions/a2p-sms",
    linkLabel: "A2P SMS",
  },
  {
    title: "Payment Verification",
    description:
      "Support a second confirmation step on higher-risk payments, either as a passcode or as a request to approve the action in your own application.",
    Icon: CreditCard,
    color: "text-sky",
    href: "/use-cases/payment-fintech",
    linkLabel: "Payment messaging",
  },
  {
    title: "Password Change Alerts",
    description:
      "Send a short notification whenever credentials are changed, which is often how a customer first learns that someone else has accessed the account.",
    Icon: BadgeCheck,
    color: "text-violet",
    href: "/use-cases/otp-verification",
    linkLabel: "OTP verification",
  },
  {
    title: "Account Recovery",
    description:
      "Deliver codes and confirmations during reset and recovery flows, so the process stays with the person who controls the registered number.",
    Icon: UserCheck,
    color: "text-lime",
    href: "/use-cases/identity-verification",
    linkLabel: "Identity verification",
  },
  {
    title: "High-Risk Activity Notifications",
    description:
      "Communicate the events your risk rules treat as unusual, from first-time payees to activity in an unexpected market, in a format customers read immediately.",
    Icon: Activity,
    color: "text-navy",
    href: "/use-cases/platform-notifications",
    linkLabel: "Platform notifications",
  },
];

const deliveryFactors = [
  {
    title: "Latency",
    description:
      "Fraud and verification messages are read under time pressure, so consistent arrival within seconds matters as much as the overall delivery percentage.",
    Icon: Clock,
    color: "text-amber",
  },
  {
    title: "Delivery Rate",
    description:
      "An undelivered marketing message is a lost impression. An undelivered security alert is a customer who was never told about activity on their account.",
    Icon: SignalHigh,
    color: "text-rose",
  },
  {
    title: "Route Quality",
    description:
      "Direct, well-understood routes behave predictably. Long intermediary chains introduce variability that is difficult to diagnose after the fact.",
    Icon: RouteIcon,
    color: "text-indigo",
  },
  {
    title: "Sender ID",
    description:
      "A recognisable, correctly registered sender helps the customer trust a security message, and helps that message reach the handset where registration rules apply.",
    Icon: BadgeCheck,
    color: "text-violet",
  },
  {
    title: "Filtering",
    description:
      "Operator filtering can hold or drop traffic based on content and sending patterns, which is why message structure and route choice are handled together.",
    Icon: Filter,
    color: "text-sky",
  },
  {
    title: "Redundancy",
    description:
      "Alternative routing paths matter when a network or connection degrades, so security traffic does not depend on a single option.",
    Icon: Layers,
    color: "text-lime",
  },
  {
    title: "Monitoring",
    description:
      "Delivery visibility turns a vague report about missing alerts into a specific destination, operator and time window your team can act on.",
    Icon: Activity,
    color: "text-pink",
  },
  {
    title: "Delivery Visibility",
    description:
      "A message accepted by a platform is not the same as a message read by a customer. Reporting has to reflect what happened at the handset, not just at submission.",
    Icon: Eye,
    color: "text-navy",
  },
];

const securityLayers = [
  {
    title: "Fraud Detection Systems",
    description:
      "Rules, models and monitoring inside your own stack decide what counts as suspicious. SMS carries the outcome of that decision to the customer.",
    Icon: ShieldAlert,
    color: "text-rose",
  },
  {
    title: "Risk Scoring",
    description:
      "Where a score sits between clearly safe and clearly fraudulent, a message supports an additional check instead of an outright block.",
    Icon: Gauge,
    color: "text-amber",
  },
  {
    title: "Authentication",
    description:
      "Passcodes and step-up checks confirm that the person acting on the account controls the registered phone number.",
    Icon: Lock,
    color: "text-indigo",
  },
  {
    title: "Account Monitoring",
    description:
      "Continuous monitoring produces the events worth telling a customer about, from new devices to changes in spending behaviour.",
    Icon: Activity,
    color: "text-sky",
  },
  {
    title: "Email Notifications",
    description:
      "Email suits longer explanations and records, while SMS covers the moment something needs to be seen quickly.",
    Icon: Mail,
    color: "text-violet",
  },
  {
    title: "Push Notifications",
    description:
      "Push works well for engaged app users, but depends on the app being installed and notifications enabled, so SMS remains the wider fallback.",
    Icon: Smartphone,
    color: "text-lime",
  },
];

const faqs = [
  {
    question: "What is fraud prevention SMS?",
    answer:
      "Fraud prevention SMS is transactional messaging used to tell customers about security-relevant activity on their account, or to ask them to confirm it. Typical examples include suspicious transaction alerts, unusual login notifications, credential change confirmations, high-risk activity warnings and step-up verification requests. The messages are triggered by the business's own fraud detection or risk systems rather than by a marketing schedule, and the recipient is expected to read and often act on them straight away. Because of that, they are routed as time-critical transactional traffic rather than promotional traffic.",
  },
  {
    question: "How is SMS used for fraud alerts?",
    answer:
      "A fraud detection system or risk engine identifies activity that looks unusual, such as a payment that does not match the customer's normal behaviour or a login from an unfamiliar device. The business then sends a short SMS describing the activity and, where relevant, asking the customer to confirm, reject or verify it. SMS is chosen because it reaches almost any mobile phone without an app, a data connection or push permissions, and because people tend to read text messages quickly. The message is the communication layer, not the detection mechanism itself.",
  },
  {
    question: "Why do fraud alert SMS messages get delayed?",
    answer:
      "Common causes include indirect routing to the destination network, operator or regulatory filtering, sender ID rules that are not met in a particular country, congestion on a shared route, and handsets that are temporarily unreachable, ported or switched off. Delays also build up when traffic passes through several intermediaries, each adding processing time. Without delivery reporting at operator level it is often unclear which factor applies to a specific destination, which is why route quality and monitoring matter as much as the wording of the alert.",
  },
  {
    question: "How can businesses improve fraud alert SMS delivery?",
    answer:
      "Send security alerts over routes intended for transactional traffic rather than the cheapest option available, and work with a provider that has direct operator connectivity in your main markets. Register sender IDs where local rules require it, keep alerts short, clear and free of patterns that attract filtering, and separate security traffic from promotional sending. Monitor delivery per destination and per operator rather than as one global figure, keep alternative routing available if a path degrades, and investigate market-specific problems at route level instead of retrying blindly.",
  },
  {
    question: "Can SMS prevent fraud?",
    answer:
      "SMS on its own does not prevent or detect fraud. Detection comes from fraud systems, risk models, account monitoring and authentication controls inside the business. What SMS provides is a fast, widely reachable way to notify a customer about what those systems found, or to ask for an additional confirmation before an action completes. Used that way it can shorten the time between suspicious activity and a customer response, which is often what limits the impact. It should be treated as a communication layer within a broader security strategy.",
  },
  {
    question: "What is the difference between a fraud alert and an OTP SMS?",
    answer:
      "An OTP SMS carries a single-use passcode that the customer types back into a login, payment or verification flow, proving they control the registered phone number. A fraud alert is informational or confirmatory: it describes activity that has been flagged and may ask the customer to approve or reject it, or to contact the business. OTPs are usually part of authentication at the moment of an action, while fraud alerts respond to risk signals around it. Both are transactional, time-sensitive messages that depend on fast, reliable delivery.",
  },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "SMS for Fraud Prevention and Security Alerts",
  serviceType: "A2P SMS messaging",
  description:
    "Transactional SMS for fraud prevention and security messaging, including suspicious transaction alerts, unusual login notifications, account security alerts, payment verification and account recovery notifications.",
  provider: {
    "@type": "Organization",
    name: "Yootelco",
    url: "https://yootelco.com",
  },
  areaServed: "Worldwide",
  audience: {
    "@type": "Audience",
    audienceType:
      "Fintech companies, banks, payment providers, ecommerce businesses, digital platforms and enterprises",
  },
  url: "https://yootelco.com/use-cases/fraud-prevention",
};

const UseCaseFraudPrevention = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="industries-page min-h-screen bg-white">
      <Seo
        title="Fraud Prevention SMS & Security Alerts | Yootelco"
        description="Use reliable SMS for fraud alerts, suspicious activity notifications and account security messaging. Improve delivery quality and reach customers quickly with Yootelco."
        path="/use-cases/fraud-prevention"
        faqs={faqs}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              Security Messaging Use Case
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              SMS for Fraud Prevention &amp; Security Alerts
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Businesses use SMS to tell customers about suspicious activity, unusual transactions, account security events and verification steps that need attention now. Yootelco delivers that traffic with a telecom-native approach: quality routing, timely delivery, monitoring and operational visibility when a destination needs attention.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
              {[
                { label: "Fraud Alert SMS", Icon: ShieldAlert },
                { label: "Security Notifications", Icon: Lock },
                { label: "Transactional A2P SMS", Icon: SignalHigh },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
                  <item.Icon className="h-4 w-4 text-primary" strokeWidth={2.2} />
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">SMS as the Communication Layer for Security Events</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            It is worth being precise about what SMS does here. SMS does not detect fraud. Detection comes from fraud systems, risk engines, device intelligence and account monitoring inside your own platform, which decide that a transaction looks unusual or that a login does not match the customer's pattern. SMS is the channel used immediately afterwards, to tell the customer what happened or to ask them to verify it.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            That distinction sets the requirement. A fraud alert is not a campaign that can be resent tomorrow: its value depends almost entirely on arriving quickly and reliably, wherever the customer happens to be. The same applies to verification requests, credential change notices and recovery confirmations.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            SMS holds this role because it does not depend on an app being installed, push notifications being enabled or a data connection being available. For fintech companies, banks, payment providers, ecommerce businesses and digital platforms, that reach is why security messaging is treated as{" "}
            <Link to="/solutions/a2p-sms" className="font-semibold text-primary underline-offset-4 hover:underline">
              A2P SMS
            </Link>{" "}
            infrastructure rather than a marketing channel.
          </p>
        </div>
      </section>

      {/* Why SMS Matters */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Why SMS Matters for Fraud Prevention</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Almost all fraud-related messaging is triggered by an event rather than a schedule, and in nearly every case the customer either needs to act or needs to know. These are the scenarios where timely communication has the most practical effect.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {whyItMatters.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            What connects them is the window for a response: a warning read the following morning is a record of something already finished, while the same message read within a minute gives the customer a decision to make.
          </p>
        </div>
      </section>

      {/* Challenges */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Challenges With Fraud Alert SMS</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Most problems with security messaging are not caused by the message. They come from how traffic is routed, how each destination network treats it, and how much visibility the sending team has when conditions change. These issues affect how quickly a customer can be reached and how quickly they can respond.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {challenges.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            None of these issues cause fraud, and no provider removes all of them, since operator behaviour and local regulation sit outside any single supplier's control. What they affect is communication: how soon someone learns about activity on their account, and how long a verification step takes. Those are the parts route quality, monitoring and troubleshooting can improve.
          </p>
        </div>
      </section>

      {/* How Yootelco Supports */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">How Yootelco Supports Fraud Prevention Messaging</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Yootelco does not detect or prevent fraud, and we do not claim to. What we provide is the messaging layer your security systems depend on, built around route quality, monitoring and hands-on support rather than volume for its own sake.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {howWeSupport.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Teams that send security alerts usually send authentication traffic too, so the same approach covers{" "}
            <Link to="/use-cases/otp-verification" className="font-semibold text-primary underline-offset-4 hover:underline">
              OTP verification
            </Link>{" "}
            and{" "}
            <Link to="/solutions/two-factor-authentication" className="font-semibold text-primary underline-offset-4 hover:underline">
              two-factor authentication
            </Link>{" "}
            alongside alerting.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Fraud Prevention SMS Use Cases</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            The specific alerts differ between a payment platform, a lender, a marketplace and a digital wallet, but the message types overlap heavily. These are the ones security and product teams bring to us most often.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {useCases.map((item) => (
            <article
              key={item.title}
              className="flex flex-col rounded-[18px] border border-border/60 bg-card px-6 py-7 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} className="mx-auto block w-fit" />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] flex-1 text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
              <Link to={item.href} className="mt-4 text-[13px] font-bold text-primary underline-offset-4 hover:underline">
                {item.linkLabel}
              </Link>
            </article>
          ))}
        </div>
      </section>

      {/* Delivery Quality */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Why Delivery Quality Matters for Fraud Alerts</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            A message being sent is not the same as a customer receiving it quickly. Traffic can be accepted by a platform, passed between intermediaries, held by a filter and delivered long after the customer needed it, or dropped without a meaningful error. That gap between submission and arrival is where delivery quality lives.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {deliveryFactors.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            This is where a telecom-native approach makes a practical difference. Coming from the carrier side means route decisions, operator relationships and delivery investigation are handled as network problems rather than support tickets. You can read more about how we approach{" "}
            <Link to="/coverage-routes" className="font-semibold text-primary underline-offset-4 hover:underline">
              coverage and routing
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Global */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Global SMS Messaging for Fraud Prevention</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Fraud does not respect borders, and neither do customers. They travel, hold accounts in more than one market and receive cross-border payments. Each destination brings its own operator environment, filtering practices, sender ID expectations and traffic conditions, so alerting that performs well domestically does not automatically perform well elsewhere.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Route differences show up as varying latency and inconsistent delivery behaviour between regions. Direct carrier connectivity matters because it shortens the path between your platform and the customer's handset. Fewer intermediaries means fewer places for a message to be delayed, filtered or altered, and clearer answers when one destination starts behaving differently from the rest.
          </p>
          <div className="mt-[24px] mx-auto max-w-[640px] text-left">
            {[
              "Direct connections to mobile network operators",
              "Routing configured per destination and traffic type",
              "Delivery monitoring across international routes",
              "Support for sender ID requirements where local rules apply",
              "Route changes made when a destination underperforms",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 py-3">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <p className="text-[15px] leading-[1.55] text-muted-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SMS within a broader strategy */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">SMS as Part of a Broader Security Strategy</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Messaging works best alongside the controls that make the decisions. Detection, scoring, authentication and monitoring identify what needs attention; SMS, email and push carry that outcome to the customer. Treating SMS as the communication layer rather than the security control keeps expectations accurate on both sides.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {securityLayers.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            In practice most teams combine channels: push for engaged app users, email for detail and record keeping, and SMS for the moment something has to be seen. Businesses in{" "}
            <Link to="/industries/fintechs" className="font-semibold text-primary underline-offset-4 hover:underline">
              fintech
            </Link>{" "}
            and{" "}
            <Link to="/industries/banks-financial-institutions" className="font-semibold text-primary underline-offset-4 hover:underline">
              banking and financial services
            </Link>{" "}
            usually run all three, with SMS carrying the traffic that cannot afford to wait, including{" "}
            <Link to="/use-cases/transactional-alerts" className="font-semibold text-primary underline-offset-4 hover:underline">
              transactional alerts
            </Link>
            .
          </p>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Need Reliable Delivery for Fraud and Security Alerts?"
        description="Tell us about your fraud alert, verification and account security messaging requirements and we'll show you how Yootelco can support them."
        useCase="Fraud Prevention SMS"
      />

      {/* FAQ */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Conclusion */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Timely Security Messaging at Scale</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Fraud and security communication is judged on timing. A customer who hears about unusual activity while it is still unfolding can confirm, reject or escalate it. Delivered late, the same message is a record of something already finished, and the work your detection systems did loses most of its value at the last step.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            That last step is delivery quality: latency, route choice, sender presentation, redundancy and the visibility to see when a destination changes behaviour. Yootelco's telecom-native approach, built on more than 17 years of voice and messaging experience, direct operator connectivity, monitoring and responsive support, is designed for exactly this kind of enterprise security messaging. Talk to our team about your requirements, or use the{" "}
            <Link to="/contact" className="font-semibold text-primary underline-offset-4 hover:underline">
              contact form
            </Link>{" "}
            to share the destinations that matter most.
          </p>
          <Link
            to="/contact"
            className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
          >
            Contact Sales
          </Link>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default UseCaseFraudPrevention;
